import { useEffect, useRef, useState } from "react";
import { Alert, Anchor, Group, Loader, Paper, Stack, Text } from "@mantine/core";
import { useTranslation } from "react-i18next";
import type { CatalogueEntry } from "../catalogue/api";
import { CatalogueSearch } from "../catalogue/CatalogueSearch";
import { SyncProduct } from "../catalogue/SyncProduct";
import { useCatalogue } from "../catalogue/useCatalogue";
import { ProductSheet } from "./ProductSheet";
import styles from "../catalogue/Catalogue.module.scss";

/**
 * The catalogue, searched, and the sheet for the product picked (M1.13 §7) — the successor to the
 * lot builder.
 *
 * On a live show it is the operator's console: search, pick, feature. Before the show it builds
 * the shortlist. The two are one component because they are one gesture; only the button at the
 * end differs, and the heading says which one this is.
 *
 * Picking a second product replaces the sheet rather than re-labelling it — the sheet is keyed by
 * the variant, so what was typed for one product never carries over to the next.
 *
 * **Picking scrolls the sheet into view.** It opens under the results, and with a dozen of them on
 * screen that is below the fold: the host clicked, nothing visibly happened, and the price and the
 * button they need next were somewhere down the page.
 */
export function ProductPicker({
  showId,
  mode,
  onChanged,
}: {
  showId: string;
  mode: "feature" | "shortlist";
  onChanged: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const { loaded, prepared, settings, error, refresh } = useCatalogue();
  const [picked, setPicked] = useState<CatalogueEntry | undefined>(undefined);
  /** The by-hand sync, for a product just added in Shopify that the search cannot find yet. */
  const [syncing, setSyncing] = useState(false);
  const sheet = useRef<HTMLDivElement>(null);

  /*
    On each pick, not on each render — keyed on the variant, so re-reading the catalogue under an
    open sheet does not yank the page. `nearest` moves the page only as far as it must, so a sheet
    already in view stays put; the scroll margin in the stylesheet keeps it clear of the pinned
    stats bar. Instant for anybody who has asked the system for less motion.
  */
  const pickedId = picked?.variantId;
  useEffect(() => {
    if (!pickedId) return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    sheet.current?.scrollIntoView({
      behavior: reduced ? "auto" : "smooth",
      block: "nearest",
    });
  }, [pickedId]);

  return (
    <Paper withBorder radius="lg" p="lg" className={styles.card}>
      <Stack gap="sm">
        <Text size="sm" fw={600}>
          {t(
            mode === "feature"
              ? "catalogue.featureHeading"
              : "catalogue.shortlistHeading",
          )}
        </Text>
        <Text size="xs" c="dimmed">
          {t(mode === "feature" ? "catalogue.featureHelp" : "catalogue.shortlistHelp", {
            minimum: settings?.minFeatureStock ?? 5,
          })}
        </Text>

        {error ? (
          <Alert color="orange" variant="light" radius="md">
            {t("catalogue.stale")}
          </Alert>
        ) : null}

        {!loaded ? (
          <Group gap="xs">
            <Loader size="xs" color="ink" />
            <Text size="xs" c="dimmed">
              {t("catalogue.loading")}
            </Text>
          </Group>
        ) : (
          <CatalogueSearch
            prepared={prepared}
            settings={settings}
            pickedId={picked?.variantId}
            onPick={setPicked}
            onClear={() => setPicked(undefined)}
            placeholder={t("catalogue.placeholder")}
          />
        )}

        {syncing ? (
          <SyncProduct onSynced={refresh} />
        ) : (
          <Anchor
            component="button"
            type="button"
            size="xs"
            onClick={() => setSyncing(true)}
          >
            {t("catalogue.sync.notFinding")}
          </Anchor>
        )}

        {picked ? (
          <div ref={sheet} className={styles.sheet}>
            <ProductSheet
              key={picked.variantId}
              showId={showId}
              entry={picked}
              mode={mode}
              settings={settings}
              onDone={async () => {
                setPicked(undefined);
                await onChanged();
              }}
            />
          </div>
        ) : null}
      </Stack>
    </Paper>
  );
}
